import { useMemo, useState } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { skipToken } from "@reduxjs/toolkit/query";
import URI from "urijs";

import { useGetFreelancerLocationMismatchQuery } from "@js/apps/admin/api";
import { useGetJobQuery } from "@js/apps/jobs/api";
import { useBidsContext } from "@js/apps/jobs/context/refetch-bids-context";
import { useIdParam, useNavigate } from "@js/hooks";
import type { EmployerBid, EmployerBidListBid } from "@js/types/jobs";
import { normalizePath, removeTrailingSlash } from "@js/utils";

import { useFetchBidQuery } from "../../api";
import { getBidPath } from "../../utils/bid";
import { useFilteredBidList } from "../use-filtered-bid-list";

export const useBidDrawer = ({
  isInterviewingTab,
}: {
  isInterviewingTab?: boolean;
} = {}) => {
  const [hasReactionChanged, setHasReactionChanged] = useState<boolean>(false);
  const navigate = useNavigate();
  const { search, state, pathname } = useLocation();
  const jobId = useIdParam() as number;
  const bidId = useIdParam("bidId");
  const [params] = useSearchParams();
  const { refetchBidList } = useBidsContext();

  const { currentData: bidDetails } = useFetchBidQuery(
    { bidId: bidId as number },
    { skip: !bidId || !jobId },
  );
  const { currentData: job } = useGetJobQuery(
    { id: jobId as number },
    { skip: !jobId },
  );

  const { bidList: bids, isBidListFetching: isBidsFetching } =
    useFilteredBidList({ jobId, isInterviewingTab: !!isInterviewingTab });

  const bidIds = useMemo(() => bids.map(({ id }) => id) || [], [bids]);

  const bidToDisplay: EmployerBid | EmployerBidListBid | undefined =
    useMemo(() => {
      if (!bidId) {
        return;
      }

      if (bidDetails) {
        return bidDetails;
      }

      const bidToDisplayInBidsList = bids.find((bid) => bid.id === bidId);

      return bidToDisplayInBidsList;
    }, [bidDetails, bidId, bids]);

  const { data: locationMismatchData, isLoading: isLocationMismatchLoading } =
    useGetFreelancerLocationMismatchQuery(
      bidToDisplay?.freelancer.id ?? skipToken,
    );

  const currentBidIndex = useMemo(() => {
    const index = bidIds.findIndex((id) => id === Number(bidId));
    if (index === -1) {
      return;
    }

    return index;
  }, [bidId, bidIds]);

  const totalBidsCount = bidIds.length;

  const goToPreviousBid = () => {
    const previousBidIndex = getPreviousBidIndex({
      totalBidsCount,
      currentBidIndex,
    });

    if (typeof previousBidIndex === "undefined") {
      return;
    }

    navigate(
      `${getBidPath({ isInterviewingTab, jobId, bidId: bidIds[previousBidIndex] })}${search}`,
      {
        state: {
          ...state,
          noScroll: true,
          disableRemountIfTheSameLocation: true,
        },
      },
    );
  };

  const goToNextBid = () => {
    const nextBidIndex = getNextBidIndex({
      totalBidsCount,
      currentBidIndex,
    });
    if (typeof nextBidIndex === "undefined") {
      return;
    }

    navigate(
      `${getBidPath({ jobId, isInterviewingTab, bidId: bidIds[nextBidIndex] })}${search}`,
      {
        state: {
          ...state,
          noScroll: true,
          disableRemountIfTheSameLocation: true,
        },
      },
    );
  };

  const handleExited = () => {
    const navigatePath = getPathToNavigate({
      state,
      jobId,
      params,
      currentPath: pathname,
      isInterviewingTab,
    });

    navigate(navigatePath, {
      state: { noScroll: true },
    });

    if (refetchBidList && hasReactionChanged) {
      refetchBidList();
    }
  };

  return {
    currentBidIndex,
    bidToDisplay,
    totalBidsCount,
    goToNextBid,
    goToPreviousBid,
    handleExited,
    setHasReactionChanged,
    locationMismatchData,
    job,
    isBidsFetching,
    isLocationMismatchLoading,
  };
};

export const getPreviousBidIndex = ({
  totalBidsCount,
  currentBidIndex,
}: {
  totalBidsCount: number;
  currentBidIndex: number | undefined;
}) => {
  if (typeof currentBidIndex === "undefined") {
    return 0;
  }
  if (totalBidsCount <= 1) {
    return;
  }

  if (currentBidIndex === 0) {
    return totalBidsCount - 1;
  }

  return currentBidIndex - 1;
};

export const getNextBidIndex = ({
  totalBidsCount,
  currentBidIndex,
}: {
  totalBidsCount: number;
  currentBidIndex: number | undefined;
}) => {
  if (typeof currentBidIndex === "undefined") {
    return 0;
  }

  if (totalBidsCount <= 1) {
    return;
  }

  if (currentBidIndex === totalBidsCount - 1) {
    return 0;
  }

  return currentBidIndex + 1;
};

type GetPathToNavigateConfig = {
  state: Record<string, unknown> & { prevPath?: string };
  jobId?: number;
  params: URLSearchParams;
  currentPath: string;
  isInterviewingTab?: boolean;
};

export const getPathToNavigate = ({
  state,
  jobId,
  params,
  currentPath,
  isInterviewingTab,
}: GetPathToNavigateConfig) => {
  params.delete("tab");

  const bidListView = isInterviewingTab ? "interviews" : "proposals";
  const defaultPathToNavigate = `/jobs/${jobId}/${bidListView}/${params.toString() ? `?${params.toString()}` : ""}`;

  if (!state?.prevPath || state.prevPath.startsWith("/auth")) {
    return defaultPathToNavigate;
  }

  const url = new URI(state.prevPath);

  if (normalizePath(currentPath) === normalizePath(url.pathname())) {
    return defaultPathToNavigate;
  }

  if (state.prevPath.includes("?tab")) {
    // Remove bidId to prevent from opening bid drawer
    url.segment(url.segment().filter(Boolean).length - 1, "");
    url.removeQuery("tab");
  }

  return removeTrailingSlash(url.toString());
};
